import React, { Suspense, useCallback, useContext, useMemo } from "react";
import type { OptionProps, PlaceholderProps, SingleValue, ValueContainerProps } from "react-select";

import { faTrashCan, faUserCheck, faUser as faUserLight } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisH, faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import useParticipantsOptions, {
	type Participant,
	type ParticipantsReducerAction,
} from "~/calendar/hooks/useParticipantsOptions";
import { AttendeeWeeklyHours, MeetingAttendees, MeetingCostEditPanel } from "~/calendar/MeetingGuidelinesPrompts";
import { AppContext } from "~/common";
import useToggle from "~/lib/hooks/useToggle";
import { Avatar, LoadingSpinner, Tooltip } from "~/ui";
import { ButtonLink, IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";
import AsyncCreatableSelector, {
	components,
	type ControlProps,
	type SelectorOption,
} from "~/ui/selectors/AsyncCreatableSelector";

import type { MeetingCostSettings, MeetingGuidelinesSettings } from "../CalendarPage";
import AttendeeStatusAvatar from "./AttendeeStatusAvatar";

import type { useParticipantsOptions_viewer$key } from "~/__generated__/useParticipantsOptions_viewer.graphql";

import eventFormStyles from "./EventForm.module.css";
import styles from "./ParticipantsSelector.module.css";

type ParticipantSelectorOption = SelectorOption & Participant;

const BlankComponent: React.FC = () => null;

function ParticipantOption(props: OptionProps<ParticipantSelectorOption, false>) {
	const { data } = props;
	const user = useMemo(() => ({ ...data, fullName: data.fullName || data.email }), [data]);
	return (
		<components.Option {...props}>
			<Avatar user={user} className={styles.optionAvatar} />
			<span className={styles.optionText}>{user.fullName}</span>
		</components.Option>
	);
}

function ParticipantInputControl({ children, ...props }: ControlProps<ParticipantSelectorOption, false>) {
	return (
		<components.Control {...props} className={styles.control}>
			<FontAwesomeIcon icon={faUser} className={cx(eventFormStyles.formSectionRowIcon, styles.icon)} />
			{children}
		</components.Control>
	);
}

function ParticipantInputValueContainer({ children, ...props }: ValueContainerProps<ParticipantSelectorOption, false>) {
	return (
		<components.ValueContainer {...props} className={styles.valueContainer}>
			{children}
		</components.ValueContainer>
	);
}

function ParticipantInputPlaceholder({ ...props }: PlaceholderProps<ParticipantSelectorOption, false>) {
	const { isFocused } = props;
	return (
		<components.Placeholder {...props}>
			{isFocused ? "Add by name or email…" : "Add participants…"}
		</components.Placeholder>
	);
}

function Organizer() {
	const { user } = useContext(AppContext);
	return (
		<div className={cx(styles.participant, styles.withSubLabel)}>
			{user && <Avatar user={user} className={styles.avatar} />}
			<div className={styles.participantProfile}>
				<Tooltip
					content={`${user?.fullName} (you)`}
					placement="top"
					disabled={user?.fullName ? user?.fullName.length < 30 : true}
				>
					<span className={styles.participantName}>{user?.fullName} (you)</span>
				</Tooltip>
				<small>Organizer</small>
			</div>
		</div>
	);
}

interface SelectedParticipantProps extends Participant {
	removeSelf?: () => void;
	setOptional?: (isOptional?: boolean) => void;
	attendeeWeeklyMeetingHoursWarningEnabled?: boolean;
}

function SelectedParticipant({
	removeSelf,
	setOptional,
	attendeeWeeklyMeetingHoursWarningEnabled,
	...participantData
}: SelectedParticipantProps) {
	const participant = useMemo(
		() => ({ ...participantData, fullName: participantData.fullName || participantData.email }),
		[participantData],
	);

	const toggleOptional = useCallback(
		() => setOptional?.(!participant.isOptional),
		[setOptional, participant.isOptional],
	);

	return (
		<div className={cx(styles.participant, { [styles.withSubLabel]: participant.isOptional })}>
			<AttendeeStatusAvatar
				user={participant}
				outline={participant.isGuest ? "none" : "solid"}
				responseStatus={participant.responseStatus || ""}
				className={styles.avatar}
			/>
			<div className={styles.participantProfile}>
				<Tooltip
					content={participant.fullName}
					placement="top"
					disabled={participant.fullName ? participant.fullName.length < 30 : true}
				>
					<span className={styles.participantName}>{participant.fullName}</span>
				</Tooltip>
				{participant.isOptional && <small>Optional</small>}
			</div>
			{attendeeWeeklyMeetingHoursWarningEnabled && !participant.isOptional && (
				<AttendeeWeeklyHours attendee={participant} />
			)}
			<Menu>
				<Menu.Trigger>
					<IconButton icon={faEllipsisH} className={styles.participantControls} />
				</Menu.Trigger>
				<Menu.Content align="end">
					<Menu.Item icon={participant.isOptional ? faUserCheck : faUserLight} onSelect={toggleOptional}>
						{participant.isOptional ? "Mark required" : "Mark optional"}
					</Menu.Item>
					<Menu.Item icon={faTrashCan} destructive onSelect={removeSelf}>
						Remove
					</Menu.Item>
				</Menu.Content>
			</Menu>
		</div>
	);
}

interface ParticipantsSelectorProps {
	selectedParticipants: Participant[];
	changeParticipants: React.Dispatch<ParticipantsReducerAction>;
	viewer: useParticipantsOptions_viewer$key;
	meetingGuidelinesSettings: MeetingGuidelinesSettings;
	meetingCostSettings: MeetingCostSettings;
	setMeetingAttendeesRendered: (rendered: boolean) => void;
}

function ParticipantsSelector({
	selectedParticipants,
	changeParticipants,
	viewer,
	meetingGuidelinesSettings,
	meetingCostSettings,
	setMeetingAttendeesRendered,
}: ParticipantsSelectorProps) {
	const { loadOptions, getNewOptionData, isValidNewOption } = useParticipantsOptions(viewer, selectedParticipants);

	const onSelect = useCallback(
		(participant: SingleValue<ParticipantSelectorOption>) => {
			participant && changeParticipants({ participant, type: "ADD_PARTICIPANT" });
		},
		[changeParticipants],
	);

	const changeParticipantsFn = useCallback(
		(action: ParticipantsReducerAction) => (isOptional?: boolean) => changeParticipants({ ...action, isOptional }),
		[changeParticipants],
	);

	const [showAll, toggleShowAll] = useToggle(false);

	return (
		<>
			<AsyncCreatableSelector
				loadOptions={loadOptions}
				value={null}
				components={{
					IndicatorsContainer: BlankComponent,
					Placeholder: ParticipantInputPlaceholder,
					Control: ParticipantInputControl,
					ValueContainer: ParticipantInputValueContainer,
					Option: props => (
						<ParticipantOption
							{...props}
							isDisabled={!!selectedParticipants.find(({ id }) => id === props.data.id) || props.isDisabled}
						/>
					),
				}}
				defaultOptions
				allowCreateWhileLoading={false}
				getNewOptionData={getNewOptionData}
				isValidNewOption={isValidNewOption}
				menuPosition="fixed"
				onChange={onSelect}
			/>
			{meetingCostSettings?.meetingCostEnabled && meetingCostSettings?.meetingCostEnforceWorkspaceSettings && (
				<Suspense fallback={<LoadingSpinner />}>
					<MeetingCostEditPanel attendees={selectedParticipants} />
				</Suspense>
			)}
			{meetingGuidelinesSettings?.meetingAttendeesEnabled && (
				<MeetingAttendees
					attendees={selectedParticipants}
					maxRecommendedAttendees={meetingGuidelinesSettings?.maxRecommendedAttendees}
					setRendered={setMeetingAttendeesRendered}
				/>
			)}
			<div className={styles.participants}>
				<Organizer />
				{(showAll ? selectedParticipants : selectedParticipants.slice(0, 5)).map(participant => (
					<SelectedParticipant
						key={participant.id}
						{...participant}
						removeSelf={changeParticipantsFn({ type: "REMOVE_PARTICIPANT", participant })}
						setOptional={changeParticipantsFn({ type: "SET_OPTIONAL", participant })}
						attendeeWeeklyMeetingHoursWarningEnabled={
							meetingGuidelinesSettings?.attendeeWeeklyMeetingHoursWarningEnabled
						}
					/>
				))}
				{!showAll && selectedParticipants.length > 5 && (
					<span className={styles.hiddenParticipants}>
						+{selectedParticipants.length - 5} participant{selectedParticipants.length > 6 && "s"} —&nbsp;
						<ButtonLink onClick={toggleShowAll} className={styles.showAllButton}>
							Show all
						</ButtonLink>
					</span>
				)}
			</div>
		</>
	);
}

export default React.memo(ParticipantsSelector);
